import { useEffect, useState, useCallback } from "react";
import { debounce } from "lodash";
import { useDispatch, useSelector } from "react-redux";
import { Filter } from "lucide-react";
import Box from '@mui/material/Box';
import { Button } from '../../../components/ui/button';
import DataTable from "../../../components/ui/data-table/data-table";
import { createActionsColumn } from "../../../components/ui/data-table/columns";
import { SearchInput } from "../../../components/ui/search-input";
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import tollService from "services/TollChargeService";
import LocationDropdown from "components/LocationDropdown/LocationDropdown";
import { FormDialog } from "../../../components/ui/form-dialog";
import AddTollForm from "../../../components/TollForm/AddTollForm";
import { getCar } from "redux/actions/UserCar";
import { NewPagination } from "../../../components/ui/newPagination";
import CommonDialog from "components/commonDialog/CommonDialogV2";
import VehicleTypeDropdown from "components/VehicleTypeDropdown/VehicleTypeDropdown";
import FuelTypeDropdown from "components/FuelTypeDropdown/FuelTypeDropdown";

export default function TollCharge() {
  const dispatch = useDispatch();
  const [tollData, setTollData] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [filteredData, setFilteredData] = useState([]);
  const [columns, setColumns] = useState([]);
  const [isAddTollOpen, setIsAddTollOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingTollId, setEditingTollId] = useState(null);
  const [carOptions, setCarOptions] = useState([]);
  const [formData, setFormData] = useState({
    toll: "",
    charges: "",
    vehicleType: "",
    fuelType: "",
    location: "",
  });

  const [filterVehicleType, setFilterVehicleType] = useState("");
  const [filterFuelType, setFilterFuelType] = useState("");

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [selectedLocationId, setSelectedLocationId] = useState(null);

  const selectedLocation = useSelector((state) => state?.locationDropdown?.selectedLocation);
  const carData = useSelector((state) => state?.userCar?.userCar?.data?.body);
  const [selectedCarType, setSelectedCarType] = useState("");

  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [deleteTollId, setDeleteTollId] = useState(null);
  const [deleteTollName, setDeleteTollName] = useState("");

  const fetchTolls = async (locationId = selectedLocationId, search, vehicleType, fuelType, page = currentPage, limit = itemsPerPage) => {
    try {
      let query = {};
      if (searchQuery || search) {
        const searchParam = search || searchQuery;
        query = { ...query, search: encodeURIComponent(searchParam.trim()) };
      }
      if (locationId) {
        query = { ...query, location: locationId };
      }
      if (vehicleType) {
        query = { ...query, vehicleType };
      }
      if (fuelType) {
        query = { ...query, fuelType };
      }
      query = { ...query, page, limit };

      const res = await tollService.getTollList(query);
      const headers = res?.data?.header || [];
      const body = res?.data?.body || [];
      setTotalItems(res?.data?.totalCount || 0);

      const dynamicColumns = headers.map(({ key, label }) => ({
        accessorKey: key,
        header: key === "charges" ? "Charges (₹)" : label,
      }));

      const actionsColumn = createActionsColumn([
          {
            label: "Edit",
            onClick: (row) => {
              setFormData({
                toll: row.toll,
                charges: row.charges,
                vehicleType: row.MasterCarType?.id,
                location: row.location,
                fuelType: row.fuelType
              });
              setSelectedCarType(row.MasterCarType?.id);
              setEditingTollId(row.id);
              setIsEditing(true);
              setIsAddTollOpen(true);
            },
          },
          {
            label: "Delete",
            onClick: (row) => {
              setDeleteTollId(row.id);
              setDeleteTollName(row.toll);
              setIsDeleteDialogOpen(true);
            },
          }
        ], true);
      setTollData(body);
      setColumns([...dynamicColumns, actionsColumn]);
      setFilteredData(body);
    } catch (error) {
      toast({ title: "Error", description: "Failed to fetch toll list", variant: "destructive" });
    }
  };

  useEffect(() => {
    fetchTolls(selectedLocation?.id, undefined, filterVehicleType, filterFuelType);
    dispatch(getCar());
  }, []);

  useEffect(() => {
    if (carData && Array.isArray(carData)) {
      const options = carData.map((car) => ({
        value: car.id,
        label: car.carType,
      }));
      setCarOptions(options);
    }
  }, [carData]);

  useEffect(() => {
    fetchTolls(selectedLocation?.id, searchQuery, filterVehicleType, filterFuelType);
  }, [filterVehicleType, filterFuelType, searchQuery]);

  const handleLocationChange = useCallback((location) => {
    
    setCurrentPage(1);
    setSelectedLocationId(location?.id || null);
    fetchTolls(location?.id, searchQuery, filterVehicleType, filterFuelType);
  }, [filterVehicleType, filterFuelType, searchQuery]);

  const handleAddToll = async () => {
    if (!formData.toll || !formData.charges || !formData.vehicleType || !formData.location) return;

    try {
      const payload = {
        toll: formData.toll,
        charges: parseFloat(formData.charges),
        location: formData.location,
        vehicleType: formData.vehicleType,
        fuelType: formData.fuelType
      };

      if (isEditing && editingTollId) {
        const res = await tollService.updateToll(editingTollId, payload);
        toast.success(res?.message || "Toll updated successfully");
      } else {
        const res = await tollService.createToll(payload);
        toast.success(res?.message || "Toll added successfully");
      }

      // ✅ Reset state
      setFormData(getDefaultFormData());
      setIsEditing(false);
      setEditingTollId(null);
      setIsAddTollOpen(false);

      // ✅ Refresh list
      const locationId = selectedLocation?.id || payload.location;
      setCurrentPage(1); // Optional: reset to first page
      fetchTolls(locationId, undefined, filterVehicleType, filterFuelType, 1, itemsPerPage);
    } catch (error) {
      const apiMessage =
        error?.response?.data?.message ||
        (typeof error?.response?.data === "string" ? error.response.data : null) ||
        error?.message ||
        (isEditing ? "Failed to update toll" : "Failed to add toll");
      toast.error(apiMessage);
    }
  };


  const handleCancel = () => {
    setFormData(getDefaultFormData());
    setIsEditing(false);
    setEditingTollId(null);
    setIsAddTollOpen(false);
    setSelectedCarType("");
  };

  const debouncedFetchTolls = useCallback(
    debounce((locationId, query) => {
      fetchTolls(locationId, query, filterVehicleType, filterFuelType);
    }, 500),
    [filterVehicleType, filterFuelType]
  );

  const handleSearch = (query) => {
    setSearchQuery(query || "");
    debouncedFetchTolls(selectedLocation?.id || selectedLocationId, query || "");
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    fetchTolls(selectedLocation?.id, searchQuery, filterVehicleType, filterFuelType, page, itemsPerPage);
  };

  const handleItemsPerPageChange = (newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
    fetchTolls(selectedLocation?.id, searchQuery, filterVehicleType, filterFuelType, 1, newItemsPerPage);
  };

  useEffect(() => {
    return () => {
      debouncedFetchTolls.cancel();
    };
  }, [debouncedFetchTolls]);

  const handleOpenAddToll = () => {
    setIsEditing(false);
    setEditingTollId(null);
    setFormData(getDefaultFormData());
    setSelectedCarType("");
    setIsAddTollOpen(true);
  };

  const getDefaultFormData = () => ({
    toll: "",
    charges: "",
    vehicleType: "",
    location: "",
    fuelType: ""
  });

  const fuelTypeOptions = [
    { value: 'Petrol', label: 'Petrol' },
    { value: 'Diesel', label: 'Diesel' },
    { value: 'CNG', label: 'CNG' }
  ];

  return (
    <div className="mx-auto pt-4 pb-0">
      <h1 className="text-xl mb-6 text-[#0f538d]">Toll Management</h1>
      <div className="mb-6 flex flex-wrap items-center justify-between gap-[15px]">
        <div className="flex flex-wrap items-center gap-4 w-full lg:w-auto">
          <Box sx={{ width: { xs: '100%', sm: '200px', lg: '200px' } }}>
            <LocationDropdown
              onChange={handleLocationChange}
              value={selectedLocation}
              selected={selectedLocation}
              placeholder="All City"
              fullWidth
            />
          </Box>
          <Box sx={{ width: { xs: '100%', sm: '200px', lg: '200px' } }}>
            <VehicleTypeDropdown
              value={carOptions.find(opt => opt.value === filterVehicleType) || null}
              onChange={opt => {
                const value = opt ? opt.value : "";
                setFilterVehicleType(value);
                setSelectedCarType(value);
              }}
              options={carOptions}
              placeholder="Vehicle Type"
              disableClearable={false}
            />
          </Box>
          <Box sx={{ width: { xs: '100%', sm: '200px', lg: '200px' } }}>
            <FuelTypeDropdown
              value={fuelTypeOptions.find(opt => opt.value === filterFuelType) || null}
              onChange={opt => setFilterFuelType(opt ? opt.value : "")}
              options={fuelTypeOptions}
              placeholder="Fuel Type"
              disableClearable={false}
            />
          </Box>
        </div>

        <div className="flex flex-wrap items-center gap-2 w-full lg:w-auto">
          <SearchInput onSearch={handleSearch} className="flex-1 lg:flex-none w-full lg:w-[250px]" />
          <FormDialog
            trigger={
              <Button
                className="h-[40px] bg-[#ffa726] hover:bg-[#ffa726] rounded-[5px] font-normal text-[14px] text-white border border-white capitalize w-full lg:w-auto"
                onClick={handleOpenAddToll}
              >
                + Add Toll
              </Button>
            }
            title={isEditing ? "Edit" : "Add"}
            subtitle="Toll"
            isOpen={isAddTollOpen}
            onOpenChange={setIsAddTollOpen}
            onSubmit={handleAddToll}
            onCancel={handleCancel}
            submitLabel={isEditing ? "Update" : "Create"}
            submitDisabled={!formData.toll || !formData.charges || !formData.vehicleType || !formData.location}
          >
            <AddTollForm
              formData={formData}
              onFormDataChange={setFormData}
              vehicleTypes={carOptions}
              fuelType={fuelTypeOptions}
            />
          </FormDialog>
        </div>
      </div>

      <div>
        <DataTable
          columns={columns}
          data={filteredData}
          onRowClick={(row) => toast({ title: "Row Clicked", description: `You clicked ${row.toll}` })}
        />
        <NewPagination
          currentPage={currentPage}
          totalItems={totalItems}
          itemsPerPage={itemsPerPage}
          onPageChange={handlePageChange}
          onItemsPerPageChange={handleItemsPerPageChange}
          className="border-t"
        />
      </div>

      <CommonDialog
        open={isDeleteDialogOpen}
        handleClose={() => setIsDeleteDialogOpen(false)}
        toggle={setIsDeleteDialogOpen}
        onConfirmDialog={async () => {
          try {
            const res = await tollService.deleteToll(deleteTollId);
            toast.success(res?.message || `Toll ${deleteTollName} deleted successfully`);
            setIsDeleteDialogOpen(false);
            fetchTolls(selectedLocation?.id, undefined, filterVehicleType, filterFuelType);
          } catch (error) {
            const apiMessage =
              error?.response?.data?.message ||
              (typeof error?.response?.data === "string" ? error.response.data : null) ||
              error?.message ||
              "Failed to delete toll";
            toast.error(apiMessage);
            setIsDeleteDialogOpen(false);
          }
        }}
        text={`Are you sure you want to delete ${deleteTollName}?`}
        selectType="delete"
        dialogContent="tollDelete"
        typeOfDialog="Toll"
      />
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={true}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
    </div>
  );
}
